import { useMemo, useState } from 'react';

import type { BnplMethodType } from '@/utils/bnpl';

import type { ResolvedMessagingConfig } from './resolveMessagingConfig';
import { resolveMessagingConfig } from './resolveMessagingConfig';

type UseMessagingConfigInput = {
  amountCents: number;
  currency: string;
  country: string;
  eligibleMethods: BnplMethodType[];
};

/**
 * Confirming in the BNPL sheet attaches the payment to the cart before Stripe
 * redirects, which zeroes the cart's outstanding amount while this row is still
 * on screen. Re-configuring Stripe for 0 cents answers `no_content` and
 * collapses the message, so the quote keeps following the last amount the
 * customer could actually finance until the cart has a positive one again.
 */
export const useMessagingConfig = ({
  amountCents,
  currency,
  country,
  eligibleMethods,
}: UseMessagingConfigInput): ResolvedMessagingConfig | null => {
  const [quotedAmountCents, setQuotedAmountCents] = useState(amountCents);
  if (amountCents > 0 && amountCents !== quotedAmountCents) {
    setQuotedAmountCents(amountCents);
  }

  return useMemo(
    () =>
      resolveMessagingConfig({
        amountCents: quotedAmountCents,
        currency,
        country,
        eligibleMethods,
      }),
    [quotedAmountCents, currency, country, eligibleMethods],
  );
};
